import React, { useState, useRef } from 'react';
import {
    Box,
    Typography,
    Button,
    Paper,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    IconButton,
    Tooltip,
    CircularProgress,
    Alert,
    Chip,
    FormControlLabel,
    Switch,
    MenuItem,
    Select,
    FormControl,
    FormHelperText,
    InputLabel,
    type SelectChangeEvent,
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Refresh as RefreshIcon,
    Category as CategoryIcon,
    NotificationsActive as AlarmIcon,
} from '@mui/icons-material';
import { PageHeader } from '../../components/admin';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { csCZ } from '@mui/x-data-grid/locales';
import type { IotSenzorTypKonfig, IotPriorita, CreateIotSenzorTypRequest, UpdateIotSenzorTypRequest } from '../../api/JsdhNotifikaceService/generated';
import {
    useIotSenzorTypyQuery,
    useCreateIotSenzorTypMutation,
    useUpdateIotSenzorTypMutation,
    useDeleteIotSenzorTypMutation,
    useRefreshIotSenzorTypyCacheMutation,
} from '../../api/JsdhNotifikaceService/service/IotMutations';
import { useTypyUdalostiQuery, usePodtypyByTypIdQuery } from '../../api/JsdhNotifikaceService/service/AdminMutations';

const PRIORITY_OPTIONS: { value: IotPriorita; label: string; color: 'error' | 'warning' | 'info' | 'success' }[] = [
    { value: 'CRITICAL', label: 'Kritická', color: 'error' },
    { value: 'HIGH', label: 'Vysoká', color: 'warning' },
    { value: 'MEDIUM', label: 'Střední', color: 'info' },
    { value: 'LOW', label: 'Nízká', color: 'success' },
];

const DEFAULT_TYPES = [
    'SMOKE', 'CO', 'WATER_LEAK', 'HIGH_TEMP', 'GAS_LEAK',
    'TEMPERATURE', 'HUMIDITY', 'BATTERY_LOW', 'FAULT', 'OFFLINE',
    'HEARTBEAT', 'DOOR', 'MOTION'
];

const PLACEHOLDERS: { tag: string; popis: string }[] = [
    { tag: '{objekt}', popis: 'název objektu' },
    { tag: '{senzor}', popis: 'název senzoru' },
    { tag: '{umisteni}', popis: 'umístění senzoru' },
    { tag: '{hodnota}', popis: 'naměřená hodnota' },
    { tag: '{typ}', popis: 'název typu' },
    { tag: '{obec}', popis: 'obec' },
    { tag: '{ulice}', popis: 'ulice' },
    { tag: '{cislo}', popis: 'číslo popisné' },
];

const PREVIEW_CTX: Record<string, string> = {
    objekt: 'Obecní úřad',
    senzor: 'Detektor kuchyně',
    umisteni: 'kuchyně',
    hodnota: 'kouř',
    typ: 'Detektor kouře',
    obec: 'Těškovice',
    ulice: 'Hlavní',
    cislo: '1',
};

const renderPreview = (template: string | null | undefined): string => {
    if (!template) return '';
    let out = template;
    Object.entries(PREVIEW_CTX).forEach(([k, v]) => {
        out = out.split('{' + k + '}').join(v);
    });
    return out;
};

const IoTSenzorTypyPage: React.FC = () => {
    const { data: typy, isLoading, error } = useIotSenzorTypyQuery();
    const createMutation = useCreateIotSenzorTypMutation();
    const updateMutation = useUpdateIotSenzorTypMutation();
    const deleteMutation = useDeleteIotSenzorTypMutation();
    const refreshCacheMutation = useRefreshIotSenzorTypyCacheMutation();
    const { data: typyUdalosti } = useTypyUdalostiQuery();

    // Dialog state
    const [openDialog, setOpenDialog] = useState(false);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [selectedTyp, setSelectedTyp] = useState<IotSenzorTypKonfig | null>(null);
    const [formData, setFormData] = useState<CreateIotSenzorTypRequest>({
        kod: '',
        nazev: '',
        popis: '',
        vytvorPoplach: false,
        priorita: 'MEDIUM',
        aktivni: true,
        popisSablona: '',
        upresneniSablona: '',
        typUdalostiId: null,
        podtypUdalostiId: null,
        deadband: null,
        minIntervalMin: null,
    });

    const { data: podtypy } = usePodtypyByTypIdQuery(formData.typUdalostiId ?? undefined);
    // Typ senzoru bez zvoleneho typu udalosti jede na fallback "IoT" a podtyp neresi.
    const chybiPodtyp = !!formData.typUdalostiId && !formData.podtypUdalostiId;
    const popisRef = useRef<HTMLInputElement | null>(null);
    const upresneniRef = useRef<HTMLInputElement | null>(null);

    const insertTag = (
        field: 'popisSablona' | 'upresneniSablona',
        ref: React.RefObject<HTMLInputElement | null>,
        tag: string,
    ) => {
        const el = ref.current;
        const current = (formData[field] as string) || '';
        const pos = el?.selectionStart ?? current.length;
        const next = current.slice(0, pos) + tag + current.slice(pos);
        setFormData({ ...formData, [field]: next });
    };

    const handleOpenCreate = () => {
        setSelectedTyp(null);
        setFormData({
            kod: '',
            nazev: '',
            popis: '',
            vytvorPoplach: false,
            priorita: 'MEDIUM',
            aktivni: true,
            popisSablona: '',
            upresneniSablona: '',
            typUdalostiId: null,
            podtypUdalostiId: null,
            deadband: null,
            minIntervalMin: null,
        });
        setOpenDialog(true);
    };

    const handleOpenEdit = (typ: IotSenzorTypKonfig) => {
        setSelectedTyp(typ);
        setFormData({
            kod: typ.kod,
            nazev: typ.nazev,
            popis: typ.popis || '',
            vytvorPoplach: typ.vytvorPoplach,
            priorita: typ.priorita,
            aktivni: typ.aktivni,
            popisSablona: typ.popisSablona || '',
            upresneniSablona: typ.upresneniSablona || '',
            typUdalostiId: typ.typUdalostiId ?? null,
            podtypUdalostiId: typ.podtypUdalostiId ?? null,
            deadband: typ.deadband ?? null,
            minIntervalMin: typ.minIntervalMin ?? null,
        });
        setOpenDialog(true);
    };

    const handleOpenDelete = (typ: IotSenzorTypKonfig) => {
        setSelectedTyp(typ);
        setOpenDeleteDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setSelectedTyp(null);
        setFormData({
            kod: '',
            nazev: '',
            popis: '',
            vytvorPoplach: false,
            priorita: 'MEDIUM',
            aktivni: true,
            popisSablona: '',
            upresneniSablona: '',
            typUdalostiId: null,
            podtypUdalostiId: null,
            deadband: null,
            minIntervalMin: null,
        });
    };

    const handleSubmit = () => {
        if (selectedTyp) {
            const updateData: UpdateIotSenzorTypRequest = {
                nazev: formData.nazev,
                popis: formData.popis || null,
                vytvorPoplach: formData.vytvorPoplach,
                priorita: formData.priorita,
                aktivni: formData.aktivni,
                popisSablona: formData.popisSablona || null,
                upresneniSablona: formData.upresneniSablona || null,
                typUdalostiId: formData.typUdalostiId ?? null,
                podtypUdalostiId: formData.podtypUdalostiId ?? null,
                deadband: formData.deadband ?? null,
                minIntervalMin: formData.minIntervalMin ?? null,
            };
            updateMutation.mutate(
                { id: selectedTyp.id, data: updateData },
                { onSuccess: () => handleCloseDialog() }
            );
        } else {
            createMutation.mutate(formData, {
                onSuccess: () => handleCloseDialog(),
            });
        }
    };

    const handleDelete = () => {
        if (selectedTyp) {
            deleteMutation.mutate(selectedTyp.id, {
                onSuccess: () => {
                    setOpenDeleteDialog(false);
                    setSelectedTyp(null);
                },
            });
        }
    };

    const handlePriorityChange = (event: SelectChangeEvent<IotPriorita>) => {
        setFormData({ ...formData, priorita: event.target.value as IotPriorita });
    };

    const isDefaultType = (kod: string) => DEFAULT_TYPES.includes(kod);

    const columns: GridColDef[] = [
        { field: 'id', headerName: 'ID', width: 70 },
        {
            field: 'kod',
            headerName: 'Kód',
            width: 150,
            renderCell: (params) => (
                <Box sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                    {params.value}
                </Box>
            ),
        },
        { field: 'nazev', headerName: 'Název', flex: 1, minWidth: 200 },
        { field: 'popis', headerName: 'Popis', flex: 1, minWidth: 200 },
        {
            field: 'vytvorPoplach',
            headerName: 'Vytvoří poplach',
            width: 130,
            renderCell: (params) => (
                <Chip
                    icon={params.value ? <AlarmIcon /> : undefined}
                    label={params.value ? 'Ano' : 'Ne'}
                    color={params.value ? 'error' : 'default'}
                    size="small"
                    variant={params.value ? 'filled' : 'outlined'}
                />
            ),
        },
        {
            field: 'priorita',
            headerName: 'Priorita',
            width: 120,
            renderCell: (params) => {
                const option = PRIORITY_OPTIONS.find(o => o.value === params.value);
                return (
                    <Chip
                        label={option?.label || params.value}
                        color={option?.color || 'default'}
                        size="small"
                    />
                );
            },
        },
        {
            field: 'aktivni',
            headerName: 'Stav',
            width: 100,
            renderCell: (params) => (
                <Chip
                    label={params.value ? 'Aktivní' : 'Neaktivní'}
                    color={params.value ? 'success' : 'default'}
                    size="small"
                />
            ),
        },
        {
            field: 'actions',
            headerName: 'Akce',
            width: 120,
            sortable: false,
            filterable: false,
            renderCell: (params) => {
                const isDefault = isDefaultType(params.row.kod);
                return (
                    <Box>
                        <Tooltip title="Upravit">
                            <IconButton size="small" onClick={() => handleOpenEdit(params.row)}>
                                <EditIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title={isDefault ? 'Výchozí typ nelze smazat' : 'Smazat'}>
                            <span>
                                <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleOpenDelete(params.row)}
                                    disabled={isDefault}
                                >
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Box>
                );
            },
        },
    ];

    if (error) {
        return <Alert severity="error">Chyba při načítání typů senzorů: {error.message}</Alert>;
    }

    return (
        <Box>
            <PageHeader
                title="Konfigurace typů senzorů"
                icon={<CategoryIcon />}
                action={
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                            variant="outlined"
                            startIcon={<RefreshIcon />}
                            onClick={() => refreshCacheMutation.mutate()}
                            disabled={refreshCacheMutation.isPending}
                        >
                            {refreshCacheMutation.isPending ? (
                                <CircularProgress size={20} sx={{ mr: 1 }} />
                            ) : null}
                            Obnovit cache
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={handleOpenCreate}
                        >
                            Přidat typ
                        </Button>
                    </Box>
                }
            />

            <Alert severity="info" sx={{ mb: 2 }}>
                Typy senzorů s příznakem "Vytvoří poplach" při aktivaci vytvoří plnohodnotnou událost v hlavním systému
                a odešlou notifikace hasičům (stejně jako poplach z emailu HZS).
            </Alert>

            <Paper sx={{ width: '100%' }}>
                <DataGrid
                    rows={typy || []}
                    columns={columns}
                    loading={isLoading}
                    initialState={{
                        pagination: { paginationModel: { pageSize: 25 } },
                    }}
                    pageSizeOptions={[10, 25, 50]}
                    autoHeight
                    disableRowSelectionOnClick
                    localeText={csCZ.components.MuiDataGrid.defaultProps.localeText}
                />
            </Paper>

            {/* Create/Edit Dialog */}
            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {selectedTyp ? 'Upravit typ senzoru' : 'Nový typ senzoru'}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <TextField
                            label="Kód"
                            value={formData.kod}
                            onChange={(e) => setFormData({ ...formData, kod: e.target.value.toUpperCase() })}
                            required
                            fullWidth
                            disabled={!!selectedTyp}
                            inputProps={{ maxLength: 50, style: { fontFamily: 'monospace' } }}
                            helperText={selectedTyp ? 'Kód nelze měnit' : 'Např. GAS_LEAK, DOOR_OPEN (bez mezer, velká písmena)'}
                        />
                        <TextField
                            label="Název"
                            value={formData.nazev}
                            onChange={(e) => setFormData({ ...formData, nazev: e.target.value })}
                            required
                            fullWidth
                            inputProps={{ maxLength: 100 }}
                            helperText="Zobrazovaný název typu"
                        />
                        <TextField
                            label="Popis"
                            value={formData.popis || ''}
                            onChange={(e) => setFormData({ ...formData, popis: e.target.value })}
                            fullWidth
                            multiline
                            rows={2}
                            inputProps={{ maxLength: 255 }}
                        />

                        <FormControl fullWidth>
                            <InputLabel>Priorita</InputLabel>
                            <Select
                                value={formData.priorita || 'MEDIUM'}
                                label="Priorita"
                                onChange={handlePriorityChange}
                            >
                                {PRIORITY_OPTIONS.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                        <Chip
                                            label={option.label}
                                            color={option.color}
                                            size="small"
                                            sx={{ mr: 1 }}
                                        />
                                        {option.value}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.vytvorPoplach || false}
                                    onChange={(e) => setFormData({ ...formData, vytvorPoplach: e.target.checked })}
                                    color="error"
                                />
                            }
                            label={
                                <Box>
                                    <Typography>Vytvořit poplach při aktivaci</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Událost tohoto typu vytvoří záznam v hlavní tabulce událostí a odešle notifikace hasičům
                                    </Typography>
                                </Box>
                            }
                        />

                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.aktivni ?? true}
                                    onChange={(e) => setFormData({ ...formData, aktivni: e.target.checked })}
                                />
                            }
                            label="Aktivní"
                        />

                        {/* Typ a podtyp udalosti z ciselniku */}
                        <FormControl fullWidth>
                            <InputLabel>Typ události (číselník)</InputLabel>
                            <Select<number | ''>
                                value={formData.typUdalostiId ?? ''}
                                label="Typ události (číselník)"
                                onChange={(e) => setFormData({
                                    ...formData,
                                    typUdalostiId: e.target.value === '' ? null : Number(e.target.value),
                                    podtypUdalostiId: null,
                                })}
                            >
                                <MenuItem value=""><em>— nepoužít (zůstane "IoT") —</em></MenuItem>
                                {(typyUdalosti || []).map((t) => (
                                    <MenuItem key={t.id} value={t.id}>{t.nazev}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {/* HO-135: podtyp je povinný, jakmile je zvolený typ. Bez něj
                            builder dosadí za podtyp název typu a poplach pak nese tentýž
                            text dvakrát — včetně hlasového hlášení. */}
                        <FormControl fullWidth required={!!formData.typUdalostiId}
                            disabled={!formData.typUdalostiId} error={chybiPodtyp}>
                            <InputLabel>Podtyp události</InputLabel>
                            <Select<number | ''>
                                value={formData.podtypUdalostiId ?? ''}
                                label="Podtyp události"
                                onChange={(e) => setFormData({
                                    ...formData,
                                    podtypUdalostiId: e.target.value === '' ? null : Number(e.target.value),
                                })}
                            >
                                <MenuItem value=""><em>— bez podtypu —</em></MenuItem>
                                {(podtypy || []).map((p) => (
                                    <MenuItem key={p.id} value={p.id}>{p.nazev}</MenuItem>
                                ))}
                            </Select>
                            {chybiPodtyp && (
                                <FormHelperText>U zvoleného typu vyberte i podtyp události.</FormHelperText>
                            )}
                        </FormControl>

                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField
                                label="Deadband (proředění telemetrie)"
                                type="number"
                                value={formData.deadband ?? ''}
                                onChange={(e) => setFormData({ ...formData, deadband: e.target.value === '' ? null : Number(e.target.value) })}
                                fullWidth
                                helperText="Zapíše se nová hodnota jen při změně ≥ deadband (např. 0.5 °C). Prázdné = bez proředění."
                            />
                            <TextField
                                label="Min. interval (min)"
                                type="number"
                                value={formData.minIntervalMin ?? ''}
                                onChange={(e) => setFormData({ ...formData, minIntervalMin: e.target.value === '' ? null : Number(e.target.value) })}
                                fullWidth
                                helperText="Nebo zapiš aspoň po N minutách. Platí jen pro telemetrické typy."
                            />
                        </Box>

                        {/* Sablona popisu */}
                        <TextField
                            label="Šablona popisu (nepovinné)"
                            value={formData.popisSablona || ''}
                            onChange={(e) => setFormData({ ...formData, popisSablona: e.target.value })}
                            fullWidth
                            multiline
                            rows={2}
                            inputProps={{ maxLength: 500 }}
                            inputRef={popisRef}
                            helperText="Prázdné = automatický popis. Klikni na značku pro vložení."
                        />
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: -1 }}>
                            {PLACEHOLDERS.map((p) => (
                                <Tooltip key={p.tag} title={p.popis}>
                                    <Chip label={p.tag} size="small" onClick={() => insertTag('popisSablona', popisRef, p.tag)} />
                                </Tooltip>
                            ))}
                        </Box>
                        {formData.popisSablona ? (
                            <Alert severity="info" sx={{ py: 0 }}>Náhled: {renderPreview(formData.popisSablona)}</Alert>
                        ) : null}

                        {/* Sablona upresneni */}
                        <TextField
                            label="Šablona upřesnění (nepovinné)"
                            value={formData.upresneniSablona || ''}
                            onChange={(e) => setFormData({ ...formData, upresneniSablona: e.target.value })}
                            fullWidth
                            multiline
                            rows={2}
                            inputProps={{ maxLength: 500 }}
                            inputRef={upresneniRef}
                            helperText="Prázdné = automatické upřesnění. Klikni na značku pro vložení."
                        />
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: -1 }}>
                            {PLACEHOLDERS.map((p) => (
                                <Tooltip key={p.tag} title={p.popis}>
                                    <Chip label={p.tag} size="small" onClick={() => insertTag('upresneniSablona', upresneniRef, p.tag)} />
                                </Tooltip>
                            ))}
                        </Box>
                        {formData.upresneniSablona ? (
                            <Alert severity="info" sx={{ py: 0 }}>Náhled: {renderPreview(formData.upresneniSablona)}</Alert>
                        ) : null}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Zrušit</Button>
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={
                            !formData.kod?.trim() ||
                            !formData.nazev?.trim() ||
                            chybiPodtyp ||
                            createMutation.isPending ||
                            updateMutation.isPending
                        }
                    >
                        {(createMutation.isPending || updateMutation.isPending) && (
                            <CircularProgress size={20} sx={{ mr: 1 }} />
                        )}
                        {selectedTyp ? 'Uložit' : 'Vytvořit'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
                <DialogTitle>Smazat typ senzoru?</DialogTitle>
                <DialogContent>
                    <Typography>
                        Opravdu chcete smazat typ senzoru <strong>{selectedTyp?.nazev}</strong> ({selectedTyp?.kod})?
                    </Typography>
                    {isDefaultType(selectedTyp?.kod || '') && (
                        <Alert severity="error" sx={{ mt: 2 }}>
                            Výchozí typy senzorů nelze smazat!
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDeleteDialog(false)}>Zrušit</Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleDelete}
                        disabled={deleteMutation.isPending || isDefaultType(selectedTyp?.kod || '')}
                    >
                        {deleteMutation.isPending && (
                            <CircularProgress size={20} sx={{ mr: 1 }} />
                        )}
                        Smazat
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default IoTSenzorTypyPage;
